import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Home, 
  PieChart, 
  TrendingUp, 
  Target, 
  User, 
  Settings, 
  Plus,
  X,
  BarChart3
} from 'lucide-react';
import { Link, useLocation } from 'react-router';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '~/components/ui/drawer';
import { Button } from '~/components/ui/button';
import { Separator } from '~/components/ui/separator';
import { cn } from '~/lib/utils';

interface NavigationItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

const getMainNavItems = (t: any): NavigationItem[] => [
  {
    id: 'dashboard',
    label: t('navigation.dashboard'),
    href: '/',
    icon: Home,
    description: t('drawer.dashboardDescription')
  },
  {
    id: 'portfolio',
    label: t('navigation.portfolio'),
    href: '/portfolio',
    icon: PieChart,
    description: t('drawer.portfolioDescription')
  },
  {
    id: 'insights',
    label: t('navigation.insights'),
    href: '/insights',
    icon: BarChart3,
    description: t('drawer.insightsDescription')
  },
  {
    id: 'strategies',
    label: t('navigation.strategies'),
    href: '/strategies',
    icon: TrendingUp,
    description: t('drawer.strategiesDescription')
  },
  {
    id: 'goals',
    label: t('navigation.goals'),
    href: '/goals',
    icon: Target,
    description: t('drawer.freedomGoalsDescription')
  },
];

const getSecondaryNavItems = (t: any): NavigationItem[] => [
  {
    id: 'profile',
    label: t('common.profile'),
    href: '/profile',
    icon: User,
    description: t('drawer.profileDescription')
  },
  {
    id: 'settings',
    label: t('common.settings'),
    href: '/settings',
    icon: Settings,
    description: t('drawer.settingsDescription')
  },
];

interface NavigationDrawerProps {
  children: React.ReactNode;
}

export function NavigationDrawer({ children }: NavigationDrawerProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const mainNavItems = getMainNavItems(t);
  const secondaryNavItems = getSecondaryNavItems(t);

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <Drawer direction="left">
      <DrawerTrigger asChild>
        {children}
      </DrawerTrigger>
      <DrawerContent className="h-full w-80 fixed inset-y-0 left-0 z-50 flex flex-col">
        <DrawerHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <span className="text-sm font-bold">W</span>
              </div>
              <div>
                <DrawerTitle className="text-lg font-semibold">{t('app.title')}</DrawerTitle>
                <DrawerDescription className="text-sm text-muted-foreground">
                  {t('app.yourFinancialFreedomCompanion')}
                </DrawerDescription>
              </div>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <X className="h-4 w-4" />
                <span className="sr-only">{t('app.closeNavigation')}</span>
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Main Navigation */}
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('drawer.main')}</h3>
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <DrawerClose asChild key={item.id}>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center space-x-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200",
                      "hover:bg-accent hover:text-accent-foreground",
                      active 
                        ? "bg-primary text-primary-foreground shadow-sm" 
                        : "text-muted-foreground"
                    )}
                  >
                    <Icon className={cn(
                      "h-4 w-4 flex-shrink-0",
                      active ? "text-primary-foreground" : "text-muted-foreground"
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        "font-medium",
                        active ? "text-primary-foreground" : "text-foreground"
                      )}>
                        {item.label}
                      </div>
                      {item.description && (
                        <div className={cn(
                          "text-xs mt-0.5 truncate",
                          active ? "text-primary-foreground/80" : "text-muted-foreground"
                        )}>
                          {item.description}
                        </div>
                      )}
                    </div>
                  </Link>
                </DrawerClose>
              );
            })}
          </div>

          <Separator className="my-6" />

          {/* Secondary Navigation */}
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('drawer.account')}</h3>
            {secondaryNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <DrawerClose asChild key={item.id}>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center space-x-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200",
                      "hover:bg-accent hover:text-accent-foreground",
                      active 
                        ? "bg-primary text-primary-foreground shadow-sm" 
                        : "text-muted-foreground"
                    )}
                  >
                    <Icon className={cn(
                      "h-4 w-4 flex-shrink-0",
                      active ? "text-primary-foreground" : "text-muted-foreground"
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        "font-medium",
                        active ? "text-primary-foreground" : "text-foreground"
                      )}>
                        {item.label}
                      </div>
                      {item.description && (
                        <div className={cn(
                          "text-xs mt-0.5 truncate",
                          active ? "text-primary-foreground/80" : "text-muted-foreground"
                        )}>
                          {item.description}
                        </div>
                      )}
                    </div>
                  </Link>
                </DrawerClose>
              );
            })}
          </div>
        </div>

        {/* Add Asset Button */}
        <div className="border-t px-6 py-4">
          <DrawerClose asChild>
            <Link to="/assets/add/type">
              <Button className="w-full" size="lg">
                <Plus className="h-4 w-4 mr-2" />
{t('actions.addAsset')}
              </Button>
            </Link>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
